import React, { useEffect, useState } from "react";
import { Form, Input, Button, Switch, Upload, Progress, Image, App } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { customUpload } from "../../../utils";
import {
  createTiendaColegio,
  getTiendaColegios,
  updateTiendaColegio,
} from "../../../services/tienda-colegios/tienda-colegio.service";
import { CreateTiendaColegioDto } from "../../../services/tienda-colegios/tienda-colegio.types";

const { TextArea } = Input;

interface TiendaTabProps {
  schoolId: number;
  schoolName: string;
}

interface FormValues {
  nombre: string;
  slug_tienda: string;
  logo_url?: string;
  ciclo: string;
  frase?: string;
  frase_autor?: string;
  mensaje_bienvenida?: string;
  mostrar_preescolar: boolean;
  mostrar_primaria: boolean;
  tienda_activa: boolean;
}

const DEFAULT_VALUES: FormValues = {
  nombre: "",
  slug_tienda: "",
  logo_url: undefined,
  ciclo: "2026-2027",
  frase: "",
  frase_autor: "",
  mensaje_bienvenida: "",
  mostrar_preescolar: true,
  mostrar_primaria: true,
  tienda_activa: false,
};

const TiendaTab: React.FC<TiendaTabProps> = ({ schoolId, schoolName }) => {
  const { notification } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [formValues, setFormValues] = useState<FormValues>({
    ...DEFAULT_VALUES,
    nombre: schoolName,
  });
  const [loading, setLoading] = useState(false);
  const [tiendaId, setTiendaId] = useState<number | undefined>(undefined);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [progressLogo, setProgressLogo] = useState(0);

  useEffect(() => {
    setLoading(true);
    getTiendaColegios()
      .then((colegios) => {
        const existing = colegios.find((c) => c.id === schoolId);
        if (existing) {
          const vals: FormValues = {
            nombre: existing.nombre,
            slug_tienda: existing.slug_tienda,
            logo_url: existing.logo_url ?? undefined,
            ciclo: existing.ciclo,
            frase: existing.frase ?? "",
            frase_autor: existing.frase_autor ?? "",
            mensaje_bienvenida: existing.mensaje_bienvenida ?? "",
            mostrar_preescolar: existing.mostrar_preescolar,
            mostrar_primaria: existing.mostrar_primaria,
            tienda_activa: (existing as any).tienda_activa ?? false,
          };
          form.setFieldsValue(vals);
          setFormValues(vals);
          setTiendaId(existing.id);
        } else {
          const defaults = { ...DEFAULT_VALUES, nombre: schoolName };
          form.setFieldsValue(defaults);
          setFormValues(defaults);
        }
      })
      .catch((error) => {
        console.error("Error loading tienda colegios:", error);
        notification.error({
          message: "Error",
          description:
            error?.response?.data?.message ??
            "No se pudo cargar la configuración de la tienda.",
        });
      })
      .finally(() => setLoading(false));
  }, [schoolId, notification]);

  const suggestSlug = () => {
    const nombre = (form.getFieldValue("nombre") as string) || "";
    const base = nombre
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9 -]/g, "")
      .trim()
      .replace(/\s+/g, "-");
    const nn = String(Math.floor(Math.random() * 90) + 10);
    const slug = `${base}-${nn}`;
    form.setFieldsValue({ slug_tienda: slug });
    setFormValues((prev) => ({ ...prev, slug_tienda: slug }));
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const values = form.getFieldsValue();
    const dto: CreateTiendaColegioDto & { tienda_activa: boolean } = {
      school_id: schoolId,
      nombre: values.nombre,
      slug_tienda: values.slug_tienda,
      logo_url: values.logo_url,
      ciclo: values.ciclo,
      frase: values.frase || undefined,
      frase_autor: values.frase_autor || undefined,
      mensaje_bienvenida: values.mensaje_bienvenida || undefined,
      mostrar_preescolar: values.mostrar_preescolar,
      mostrar_primaria: values.mostrar_primaria,
      tienda_activa: values.tienda_activa,
    };

    setLoading(true);
    try {
      if (tiendaId !== undefined) {
        await updateTiendaColegio(tiendaId, dto);
      } else {
        const created = await createTiendaColegio(dto);
        if (created) {
          setTiendaId(created.id);
        }
      }
      notification.success({
        message: "Datos de tienda guardados",
        placement: "top",
      });
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 409) {
        notification.error({
          message: "El slug ya está en uso",
          placement: "top",
        });
      } else {
        notification.error({
          message: "Error al guardar los datos de tienda",
          placement: "top",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 700 }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ ...DEFAULT_VALUES, nombre: schoolName }}
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Tienda activa"
          name="tienda_activa"
          valuePropName="checked"
        >
          <Switch
            checked={formValues.tienda_activa}
            onChange={(v) =>
              setFormValues((prev) => ({ ...prev, tienda_activa: v }))
            }
          />
        </Form.Item>

        <Form.Item
          label="Nombre"
          name="nombre"
          rules={[
            { required: true, message: "El nombre es requerido" },
            { min: 2, message: "Mínimo 2 caracteres" },
            { max: 120, message: "Máximo 120 caracteres" },
          ]}
        >
          <Input
            value={formValues.nombre}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, nombre: e.target.value }))
            }
            onBlur={suggestSlug}
          />
        </Form.Item>

        <Form.Item
          label="Slug tienda"
          name="slug_tienda"
          rules={[
            { required: true, message: "El slug es requerido" },
            {
              pattern: /^[a-z0-9-]+-\d{2}$/,
              message:
                "Solo minúsculas, números y guiones. Debe terminar en -NN (ej: kauri-school-26)",
            },
          ]}
          extra="Solo minúsculas, números y guiones. Ej: kauri-school-26"
        >
          <Input
            value={formValues.slug_tienda}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, slug_tienda: e.target.value }))
            }
            addonAfter={
              <Button size="small" type="link" onClick={suggestSlug}>
                Sugerir slug
              </Button>
            }
          />
        </Form.Item>

        <Form.Item label="Logo" name="logo_url">
          <>
            {formValues.logo_url && (
              <div style={{ marginBottom: 8 }}>
                <Image src={formValues.logo_url} width={120} preview />
              </div>
            )}
            <Upload
              customRequest={async (options) => {
                setUploadingLogo(true);
                setProgressLogo(0);
                try {
                  const result = await customUpload(
                    {
                      ...options,
                      onProgress: ({ percent }: { percent: number }) => {
                        setProgressLogo(Math.round(percent));
                        if (options.onProgress) options.onProgress({ percent });
                      },
                    },
                    "logos"
                  );
                  if (result?.url) {
                    form.setFieldsValue({ logo_url: result.url });
                    setFormValues((prev) => ({ ...prev, logo_url: result.url }));
                  }
                } finally {
                  setUploadingLogo(false);
                }
              }}
              showUploadList={false}
              accept="image/jpeg,image/png,image/webp"
              disabled={uploadingLogo}
            >
              <Button icon={<UploadOutlined />} loading={uploadingLogo} aria-label="Subir logo de tienda">
                {formValues.logo_url ? "Cambiar logo" : "Subir logo"}
              </Button>
            </Upload>
            {uploadingLogo && (
              <Progress percent={progressLogo} size="small" style={{ marginTop: 8 }} />
            )}
          </>
        </Form.Item>

        <Form.Item
          label="Ciclo"
          name="ciclo"
          rules={[{ required: true, message: "El ciclo es requerido" }]}
        >
          <Input
            placeholder="2026-2027"
            value={formValues.ciclo}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, ciclo: e.target.value }))
            }
          />
        </Form.Item>

        <Form.Item
          label="Frase"
          name="frase"
          rules={[{ max: 200, message: "Máximo 200 caracteres" }]}
        >
          <Input
            value={formValues.frase}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, frase: e.target.value }))
            }
          />
        </Form.Item>

        <Form.Item
          label="Autor de la frase"
          name="frase_autor"
          rules={[{ max: 120, message: "Máximo 120 caracteres" }]}
        >
          <Input
            value={formValues.frase_autor}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, frase_autor: e.target.value }))
            }
          />
        </Form.Item>

        <Form.Item
          label="Mensaje de bienvenida"
          name="mensaje_bienvenida"
          rules={[{ max: 500, message: "Máximo 500 caracteres" }]}
        >
          <TextArea
            rows={3}
            value={formValues.mensaje_bienvenida}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                mensaje_bienvenida: e.target.value,
              }))
            }
          />
        </Form.Item>

        <Form.Item
          label="Mostrar preescolar"
          name="mostrar_preescolar"
          valuePropName="checked"
        >
          <Switch
            checked={formValues.mostrar_preescolar}
            onChange={(v) =>
              setFormValues((prev) => ({ ...prev, mostrar_preescolar: v }))
            }
          />
        </Form.Item>

        <Form.Item
          label="Mostrar primaria"
          name="mostrar_primaria"
          valuePropName="checked"
        >
          <Switch
            checked={formValues.mostrar_primaria}
            onChange={(v) =>
              setFormValues((prev) => ({ ...prev, mostrar_primaria: v }))
            }
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Guardar tienda
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default TiendaTab;
